import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ChevronRight, Loader2, MessageCircle, Phone, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createSupportCase, getSupportCategories } from '@/api/support';
import { friendlyError } from '@/lib/apiError';
import { useSiteSettings } from '@/lib/siteContent';
import { urgentContact } from '@/lib/support';
import { AttachmentPicker } from './Attachments';

/**
 * "Get help with this booking" — pick what went wrong, describe it, attach
 * photos, and land in the case thread. Opened from the booking page (with a
 * bookingId) or from /support (general question, no booking).
 */
export default function CreateCaseDialog({ open, onClose, bookingId = null, bookingTitle = '' }) {
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const urgent = urgentContact(settings);
  const panelRef = useRef(null);

  const [categories, setCategories] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [category, setCategory] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCategory(null);
    setSubject('');
    setDescription('');
    setAttachments([]);
    setExisting(null);
    setLoadError('');
    setCategories(null);
    getSupportCategories(bookingId)
      .then((list) => setCategories(Array.isArray(list) ? list : []))
      .catch((err) => {
        setCategories([]);
        setLoadError(friendlyError(err, "We couldn't load help topics."));
      });
  }, [open, bookingId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  // Booking-related topics only make sense with a booking.
  const visible = (categories || []).filter((c) => bookingId || !c.requiresBooking);
  const eventDay = visible.filter((c) => c.eventDay);
  const regular = visible.filter((c) => !c.eventDay);

  const choose = (c) => {
    setCategory(c);
    setSubject(bookingTitle ? `${c.label} — ${bookingTitle}` : c.label);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!category || !subject.trim() || description.trim().length < 5) return;
    setSubmitting(true);
    setExisting(null);
    try {
      const created = await createSupportCase({
        categorySlug: category.slug,
        bookingId: bookingId || undefined,
        subject: subject.trim().slice(0, 200),
        description: description.trim(),
        attachments,
      });
      toast.success(created?.caseNumber ? `Case ${created.caseNumber} created — we'll reply here` : "Case created — we'll reply here");
      onClose();
      navigate(`/support/${created.id}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        const body = err.response.data || {};
        setExisting({ id: body?.data?.caseId ?? body?.data?.id ?? body?.caseId ?? null, message: body?.message });
      } else {
        toast.error(friendlyError(err, "We couldn't create your case. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const Topic = ({ c, urgentStyle }) => (
    <li>
      <button
        type="button"
        onClick={() => choose(c)}
        className={`lk-focus flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left transition-colors ${
          urgentStyle ? 'bg-danger-tint ring-1 ring-danger/30 hover:bg-danger-tint/70' : 'bg-white ring-1 ring-ink-200 hover:bg-surface-muted'
        }`}
      >
        <span className="min-w-0">
          <span className={`block text-sm font-semibold ${urgentStyle ? 'text-danger' : 'text-ink-900'}`}>{c.label}</span>
          {c.description && <span className="mt-0.5 block text-xs text-ink-500">{c.description}</span>}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" aria-hidden="true" />
      </button>
    </li>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-ink-900/50 sm:items-center sm:p-4" onMouseDown={onClose}>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-case-title"
        onMouseDown={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-surface-muted shadow-cardHover outline-none sm:rounded-3xl"
      >
        <div className="flex items-center gap-2 border-b border-ink-200/70 bg-white px-4 py-3">
          {category && (
            <button type="button" onClick={() => setCategory(null)} aria-label="Back to topics" className="lk-focus flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-muted">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h2 id="create-case-title" className="truncate text-base font-bold text-ink-900">
              {category ? category.label : 'How can we help?'}
            </h2>
            {bookingId && <p className="truncate text-xs text-ink-500">Booking #{bookingId}{bookingTitle ? ` · ${bookingTitle}` : ''}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="lk-focus flex h-10 w-10 items-center justify-center rounded-full hover:bg-surface-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!category ? (
            categories === null ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-ink-400" aria-label="Loading" />
              </div>
            ) : (
              <div className="space-y-5">
                {loadError && <p className="rounded-xl bg-danger-tint px-4 py-3 text-sm text-danger">{loadError}</p>}

                {eventDay.length > 0 && (
                  <div className="space-y-2">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-danger">
                      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                      At the venue right now?
                    </p>
                    <ul className="space-y-2">
                      {eventDay.map((c) => (
                        <Topic key={c.slug} c={c} urgentStyle />
                      ))}
                    </ul>
                    {urgent && (
                      <div className="flex flex-wrap gap-2">
                        <a href={urgent.tel} className="lk-btn flex-1 bg-danger text-white hover:opacity-90">
                          <Phone className="h-4 w-4" />
                          Call LocalKonnect now
                        </a>
                        {urgent.whatsapp && (
                          <a href={urgent.whatsapp} target="_blank" rel="noreferrer" className="lk-btn-ghost flex-1">
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {regular.length > 0 ? (
                  <div className="space-y-2">
                    {eventDay.length > 0 && <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Something else</p>}
                    <ul className="space-y-2">
                      {regular.map((c) => (
                        <Topic key={c.slug} c={c} />
                      ))}
                    </ul>
                  </div>
                ) : (
                  !loadError && <p className="py-6 text-center text-sm text-ink-500">No help topics are available right now.</p>
                )}
              </div>
            )
          ) : (
            <form id="create-case-form" onSubmit={submit} className="space-y-4">
              {category.eventDay && urgent && (
                <div className="rounded-xl bg-danger-tint p-3 text-sm text-danger">
                  <p className="font-semibold">This is marked urgent — the organiser and our on-call team are alerted.</p>
                  <a href={urgent.tel} className="mt-2 inline-flex items-center gap-1.5 font-semibold underline">
                    <Phone className="h-4 w-4" /> Or call us now: {urgent.display}
                  </a>
                </div>
              )}
              <div>
                <label htmlFor="case-subject" className="mb-1.5 block text-sm font-medium text-ink-700">Subject</label>
                <input id="case-subject" className="lk-input" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="case-description" className="mb-1.5 block text-sm font-medium text-ink-700">What happened?</label>
                <textarea
                  id="case-description"
                  className="lk-input min-h-[8rem] resize-y"
                  value={description}
                  maxLength={4000}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us what went wrong — dates, amounts and anything you've already tried help us solve it faster."
                  required
                />
              </div>
              <AttachmentPicker value={attachments} onChange={setAttachments} disabled={submitting} />

              {existing && (
                <div role="alert" className="rounded-xl bg-warning-tint px-4 py-3 text-sm text-warning">
                  <p className="font-semibold">You already have an open case for this booking.</p>
                  <Link to={existing.id ? `/support/${existing.id}` : '/support'} onClick={onClose} className="mt-1 inline-block font-semibold underline">
                    Go to that case
                  </Link>
                </div>
              )}
            </form>
          )}
        </div>

        {category && (
          <div className="border-t border-ink-200/70 bg-white p-4">
            <button
              type="submit"
              form="create-case-form"
              disabled={submitting || !subject.trim() || description.trim().length < 5}
              className="lk-btn-primary w-full disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Send to LocalKonnect Support
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
